import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown, InputGroup, Spinner } from 'react-bootstrap'

// components
import Collapsible from '../../ui/collapsible/collapsible'
import { ArrowRight, Search } from 'react-feather'
import XeroLogo from '../../../images/xero-logo.svg'
import StripeLogo from '../../../images/stripe-logo.svg'

// definitions
import { AccountStripeOrgWithData, AccountXeroOrgWithData } from '../../../screens/subsync/subsync'
import { StripeOrgAccountXeroOrg, StripeProduct, StripeTaxRate, XeroAPIContact, XeroAccount, XeroItem, XeroTaxRate } from '../../../../../back-end/utilities/apiDefinitions'
import { MessageAction } from '../../ui/messages/message'
import { AppState } from '../../../App.d'

// utilities
import * as Request from '../../../utilities/request'

interface OrgMappingHeaderProps {
	stripeOrg: AccountStripeOrgWithData
	orgMappings: StripeOrgAccountXeroOrg[]
	orgMapping: StripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	xeroOrgs: AccountXeroOrgWithData[]
	handleOrgUpdate: (stripeOrgID: string, name: string, value: string) => void
	handleOrgAdd: (stripeOrgID: string, value: string) => void
}

const OrgMappingHeader = (props: OrgMappingHeaderProps) => {
	return (
		<Form.Group as={Row} style={{ marginBottom: '0px', marginLeft: '0px', display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
			<Col sm="1" />
			<input type="image" alt="xero-logo" style={{ width: '40px', borderRadius: '25px' }} src={StripeLogo} />
			<Col sm="2" style={{ display: 'flex', alignItems: 'center' }}>
				<div className="subsync-settings-label">{props.stripeOrg.stripeOrg!.name}</div>
			</Col>
			<Col
				sm="2"
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-evenly',
				}}
			>
				<ArrowRight></ArrowRight>
			</Col>
			<input type="image" alt="xero-logo" style={{ width: '40px' }} src={XeroLogo} />
			<Col sm="3">
				<DropdownButton
					id={'mapping-org-select'}
					title={props.mappedXeroOrg?.xeroOrg?.name || 'Select Xero Org'}
					onSelect={(eventKey) =>
						props.orgMapping
							? props.handleOrgUpdate(props.stripeOrg.stripeOrg!.stripeOrgID!, 'accountXeroOrgID', eventKey!)
							: props.handleOrgAdd(props.stripeOrg.stripeOrg!.stripeOrgID!, eventKey!)
					}
				>
					{props.mappedXeroOrg && <Dropdown.Item eventKey={''}>Remove mapping</Dropdown.Item>}
					{props.xeroOrgs
						.map((xo) => (
							<Dropdown.Item key={xo.accountXeroOrgID} eventKey={xo.accountXeroOrgID}>
								{xo.xeroOrg!.name}
							</Dropdown.Item>
						))}
				</DropdownButton>
			</Col>
		</Form.Group>)
}

interface OrgMappingContentProps {
	stripeOrg: AccountStripeOrgWithData
	orgMapping: StripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	xeroOrgs: AccountXeroOrgWithData[]
	appState: AppState
	handleOrgUpdate: (stripeOrgID: string, name: string, value: string) => void
	handleProductUpdate: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleProductAdd: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleApplyToAllProducts: (stripeOrgID: string, name: string, value: string) => void
	handleTaxRateUpdate: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
	handleTaxRateAdd: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	updateMessages: (action: MessageAction) => void
}

const OrgMappingContent = (props: OrgMappingContentProps) => {
	return props.orgMapping && props.mappedXeroOrg ? (
		<div className='pl-5 mt-5'>
			<div className='mb-2'>
				<Collapsible
					startsCollapsed={false}
					clickableElement={<p className='font-weight-bold ml-2 mt-3'>Settings</p>}
					collapsibleElement={
						<GeneralSettings
							orgMapping={props.orgMapping}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleOrgUpdate={props.handleOrgUpdate}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div className='mb-2'>
				<Collapsible
					startsCollapsed={false}
					clickableElement={<p className='font-weight-bold ml-2 mt-3'>Product Mapping</p>}
					collapsibleElement={
						<ProductMapping
							stripeProducts={props.stripeOrg.products}
							orgMapping={props.orgMapping!}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgId={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleProductUpdate={props.handleProductUpdate}
							handleProductAdd={props.handleProductAdd}
							handleApplyToAllProducts={props.handleApplyToAllProducts}
						/>
					}
				/>
			</div>
			<div className='mb-2'>
				<Collapsible
					startsCollapsed={false}
					clickableElement={<p className='font-weight-bold ml-2 mt-3'>Tax Mapping</p>}
					collapsibleElement={
						<>
							{props.stripeOrg.taxRates
								.filter((tr) => !tr.isStripeGenerated)
								.sort((a, b) => a.name === 'No Stripe Tax Rate' ? 1 : b.name === 'No Stripe Tax Rate' ? -1 : 0)
								.map((taxRate) => {
									return (
										<TaxRateMapping
											key={taxRate.stripeTaxRateID}
											stripeTaxRate={taxRate}
											orgMapping={props.orgMapping!}
											xeroTaxRates={props.mappedXeroOrg!.taxRates}
											stripeOrgId={props.stripeOrg.stripeOrg!.stripeOrgID!}
											handleTaxRateUpdate={props.handleTaxRateUpdate}
											handleTaxRateAdd={props.handleTaxRateAdd}
										/>
									)
								})}
						</>
					}
				/>
			</div>
			{props.stripeOrg.taxRates.filter((tr) => tr.isStripeGenerated).length > 0 && (
				<div className='mb-2'>
					<Collapsible
						startsCollapsed={false}
						clickableElement={<p className='font-weight-bold ml-2 mt-3'>Stripe Generated Tax Mapping</p>}
						collapsibleElement={
							<>
								{props.stripeOrg.taxRates
									.filter((tr) => tr.isStripeGenerated)
									.map((taxRate) => {
										return (
											<TaxRateMapping
												key={taxRate.stripeTaxRateID}
												stripeTaxRate={taxRate}
												orgMapping={props.orgMapping!}
												xeroTaxRates={props.mappedXeroOrg!.taxRates}
												stripeOrgId={props.stripeOrg.stripeOrg!.stripeOrgID!}
												handleTaxRateUpdate={props.handleTaxRateUpdate}
												handleTaxRateAdd={props.handleTaxRateAdd}
											/>
										)
									})}
							</>
						}
					/>
				</div>
			)}
			<div className='mb-2'>
				<Collapsible
					startsCollapsed={false}
					clickableElement={<p className='font-weight-bold ml-2 mt-3'>Stripe Fees</p>}
					collapsibleElement={
						<StripeFeesSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							xeroOrgs={props.xeroOrgs}
							appState={props.appState}
							handleSettingsUpdate={props.handleSettingsUpdate}
							updateMessages={props.updateMessages}
						/>
					}
				/>
			</div>
		</div>
	) : null
}

interface GeneralSettingsProps {
	orgMapping: StripeOrgAccountXeroOrg | undefined
	stripeOrgID: string
	handleOrgUpdate: (stripeOrgID: string, name: string, value: string) => void
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
}

const GeneralSettings = (props: GeneralSettingsProps) => {
	return (
		<>
			<Form.Group style={{ height: '2.5rem' }}>
				<Col>
					<Form.Label className="mr-3">Map Stripe products to:</Form.Label>
					<Form.Check
						inline
						type={'radio'}
						label={'Xero Accounts'}
						value={'Account'}
						checked={props.orgMapping?.stripeOrgAccountXeroOrgMappingType === 'Account'}
						onChange={() => props.handleOrgUpdate(props.stripeOrgID!, 'stripeOrgAccountXeroOrgMappingType', 'Account')}
					/>
					<Form.Check
						inline
						type={'radio'}
						label={'Xero Products'}
						value={'Item'}
						checked={props.orgMapping?.stripeOrgAccountXeroOrgMappingType === 'Item'}
						onChange={() => props.handleOrgUpdate(props.stripeOrgID!, 'stripeOrgAccountXeroOrgMappingType', 'Item')}
					/>
				</Col>
			</Form.Group>
			<Form.Group style={{ height: '2.5rem' }}>
				<Col>
					<Form.Check
						type={'checkbox'}
						id={'createZeroAmountInvoicesDisabled'}
						name={'createZeroAmountInvoicesDisabled'}
						label={"Don't create invoices in Xero when invoice amount is $0"}
						checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.createZeroAmountInvoicesDisabled}
						onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
					/>
				</Col>
			</Form.Group>
		</>
	)
}

interface ProductMappingProps {
	stripeProducts: StripeProduct[]
	stripeOrgId: string
	orgMapping: StripeOrgAccountXeroOrg
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	handleProductUpdate: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleProductAdd: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleApplyToAllProducts: (stripeOrgID: string, name: string, value: string) => void
}

const ProductMapping = (props: ProductMappingProps) => {
	const [applyToAllSelected, setApplyToAllSelected] = React.useState<boolean>(false)

	return (
		<>
			{props.stripeProducts.sort((a, b) => a.name === 'No Stripe Product' ? 1 : b.name === 'No Stripe Product' ? -1 : 0).map((product, index) => {
				const productMapping = props.orgMapping.stripeProductXeroMappings!.find(
					(mapping) => mapping.stripeProductID === product.stripeProductID
				)
				const selectedItem = productMapping
					? props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account'
						? props.mappedXeroOrg!.accounts.find((account) => account.xeroAccountID === productMapping.xeroAccountID)
						: props.mappedXeroOrg!.items.find((item) => item.xeroItemID === productMapping.xeroItemID)
					: undefined
				return (
					<Form.Group as={Row} key={product.stripeProductID}>
						<Col
							sm={3}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								width: '20vw'
							}}>
							<div className="subsync-settings-label-small">{product.name}</div>
						</Col>
						<Col
							sm={1}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: '20vw'
							}}>
							<ArrowRight size="16"></ArrowRight>
						</Col>
						<Col
							sm={4}
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '20vw'
							}}>
							<DropdownButton
								id={'mapping-account-select'}
								title={
									selectedItem
										? selectedItem.name
										: `Select Xero ${props.orgMapping.stripeOrgAccountXeroOrgMappingType}`
								}
								onSelect={(eventKey) => {
									if (productMapping) {
										props.handleProductUpdate(
											props.stripeOrgId,
											product.stripeProductID!,
											`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
											eventKey!
										)
									}
									else {
										props.handleProductAdd(
											props.stripeOrgId,
											product.stripeProductID!,
											`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
											eventKey!
										)
									}
									if (applyToAllSelected) {
										props.handleApplyToAllProducts(props.stripeOrgId, `xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`, eventKey!)
									}
								}
								}
								disabled={applyToAllSelected && index > 0}
							>
								{props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account' ? (
									<>
										<Dropdown.Header>Revenue</Dropdown.Header>
										{props.mappedXeroOrg!.accounts
											.filter((account) => account.type === 'REVENUE')
											.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
											.map((xa) => (
												<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
													{xa.name}
												</Dropdown.Item>
											))}
										<Dropdown.Header>Other Income</Dropdown.Header>
										{props.mappedXeroOrg!.accounts
											.filter((account) => account.type === 'OTHERINCOME')
											.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
											.map((xa) => (
												<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
													{xa.name}
												</Dropdown.Item>
											))}
										<Dropdown.Header>Sales</Dropdown.Header>
										{props.mappedXeroOrg!.accounts
											.filter((account) => account.type === 'SALES')
											.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
											.map((xa) => (
												<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
													{xa.name}
												</Dropdown.Item>
											))}
										<Dropdown.Header>Current Liability</Dropdown.Header>
										{props.mappedXeroOrg!.accounts
											.filter((account) => account.type === 'CURRLIAB')
											.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
											.map((xa) => (
												<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
													{xa.name}
												</Dropdown.Item>
											))}
									</>
								) : (
									props.mappedXeroOrg!.items
										.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
										.map((xi) => (
											<Dropdown.Item key={xi.xeroItemID} eventKey={xi.xeroItemID}>
												{xi.name}
											</Dropdown.Item>
										))
								)}
							</DropdownButton>
						</Col>
						<Col sm={3} style={{ width: '20vw' }}>
							{index === 0 && <Form.Check
								type={'checkbox'}
								id={'applyToAllProducts'}
								name={'stripeFeesBillsEnabled'}
								label={'Apply to All Products'}
								checked={applyToAllSelected}
								onChange={(e) => {
									setApplyToAllSelected(e.target.checked)
									if (e.target.checked && selectedItem) {
										props.handleApplyToAllProducts(props.stripeOrgId, `xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`, props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account' ? (selectedItem as XeroAccount).xeroAccountID! : (selectedItem as XeroItem).xeroItemID!)
									}
								}
								}
							/>}</Col>
					</Form.Group>
				)
			})}
		</>
	)
}

interface TaxRateMappingProps {
	stripeTaxRate: StripeTaxRate
	orgMapping: StripeOrgAccountXeroOrg
	xeroTaxRates: XeroTaxRate[]
	stripeOrgId: string
	handleTaxRateUpdate: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
	handleTaxRateAdd: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
}

const TaxRateMapping = (props: TaxRateMappingProps) => {
	const taxRateMapping = props.orgMapping!.stripeTaxRateXeroTaxRates!.find(
		(mapping) => mapping.stripeTaxRateID === props.stripeTaxRate.stripeTaxRateID
	)
	const selectedItem = props.xeroTaxRates.find((tr) => tr.xeroTaxRateID === taxRateMapping?.xeroTaxRateID)
	return (
		<Form.Group as={Row} key={props.stripeTaxRate.stripeTaxRateID}>
			<Col
				sm={3}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					width: '20vw'
				}}
			>
				<div className="subsync-settings-label-small">
					{props.stripeTaxRate.name} {props.stripeTaxRate.name !== 'No Stripe Tax Rate' && props.stripeTaxRate.percentage ? `(${(Number(props.stripeTaxRate.percentage) * 100).toFixed(2)}%)` : ''}
				</div>
			</Col>
			<Col
				sm={1}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '10vw'
				}}
			>
				<ArrowRight size="16"></ArrowRight>
			</Col>
			<Col
				sm={4}
				style={{
					display: 'flex',
					alignItems: 'center',
					width: '20vw'
				}}
			>
				<DropdownButton
					id={'mapping-taxrate-select'}
					title={taxRateMapping?.xeroTaxRateID === 'N/A' ? 'Not in use' : selectedItem?.name || 'Select Xero Tax Rate'}
					onSelect={(eventKey) =>
						taxRateMapping
							? props.handleTaxRateUpdate(props.stripeOrgId, props.stripeTaxRate.stripeTaxRateID!, 'xeroTaxRateID', eventKey!)
							: props.handleTaxRateAdd(props.stripeOrgId, props.stripeTaxRate.stripeTaxRateID!, 'xeroTaxRateID', eventKey!)
					}
				>
					{props.xeroTaxRates
						.filter((tr) => tr.canApplyToRevenue)
						.map((tr) => (
							<Dropdown.Item key={tr.xeroTaxRateID} eventKey={tr.xeroTaxRateID}>
								{tr.name} {tr.effectiveRate ? `(${(Number(tr.effectiveRate) * 100).toFixed(2)}%)` : ''}
							</Dropdown.Item>
						))}
					<Dropdown.Item eventKey="N/A">Not in use</Dropdown.Item>
				</DropdownButton>
			</Col>
			<Col sm={3} style={{ width: '20vw' }}></Col>
		</Form.Group>
	)
}

interface StripeFeesSettingsProps {
	orgMapping: StripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	xeroOrgs: AccountXeroOrgWithData[]
	appState: AppState
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	updateMessages: (action: MessageAction) => void
}

const StripeFeesSettings = (props: StripeFeesSettingsProps) => {

	// Xero Contact live search
	const [xeroContactSearch, setXeroContactSearch] = React.useState<string>('')
	const [isSearching, setIsSearching] = React.useState<boolean>(false)
	const [xeroContacts, setXeroContacts] = React.useState<XeroAPIContact[]>([])
	const [selectedXeroContactID, setSelectedXeroContactID] = React.useState<string>('')

	const handleXeroContactSearch = async (mapping: StripeOrgAccountXeroOrg) => {
		setIsSearching(true)
		try {
			const accountXeroOrg = props.xeroOrgs.find(accountXeroOrg => accountXeroOrg.accountXeroOrgID === mapping.accountXeroOrgID)
			const body = {
				userAccountXeroOrgAuthID: accountXeroOrg?.userAccountXeroOrgAuth?.id,
				tenantID: accountXeroOrg?.xeroOrg?.tenantID,
				xeroOrgID: accountXeroOrg?.xeroOrg?.xeroOrgID,
				contactName: xeroContactSearch
			}
			const res = await Request.post('xerocontact/search', body, props.appState.authState)
			setXeroContacts(res.data.data)

		} catch (e) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: 'Unable to search Xero contacts',
					timeout: 3000,
					dismissible: true,
				},
			})
		} finally {
			setIsSearching(false)
		}
	}

	return (
		<>
			<Form.Group style={{ height: '2.5rem' }}>
				<Col>
					<Form.Check
						type={'checkbox'}
						id={'stripeFeesBillsEnabled'}
						name={'stripeFeesBillsEnabled'}
						label={'Enable Bills for Stripe Fees'}
						checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
						onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
					/>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Contact
				</Form.Label>
				<Col sm={3}>
					<InputGroup>
						{props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled && <p>{xeroContacts.find(c => c.contactID === selectedXeroContactID)?.name || props.mappedXeroOrg!.contacts.find(
							(c) => c.xeroContactID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroContactID
						)?.name || 'Search to select a contact'}</p>}
					</InputGroup>
				</Col>
				{props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled ? (
					<>
						<Col sm={3}>
							<InputGroup>
								<Form.Control
									placeholder='Search Xero contacts'
									value={xeroContactSearch}
									onChange={e => setXeroContactSearch(e.target.value)}
								/>
								<InputGroup.Text>{isSearching ? <Spinner animation={'border'} size={'sm'} /> : <Search className="clickable-icon" onClick={() => handleXeroContactSearch(props.orgMapping!)} />}</InputGroup.Text>
							</InputGroup>
						</Col>
						<Col>
							<DropdownButton
								id={'settings-contact-select'}
								title={
									xeroContacts.find(
										(c) => c.contactID === selectedXeroContactID
									)?.name || xeroContacts[0]?.name || 'Select Xero Contact'
								}
								onSelect={(eventKey) => {
									setSelectedXeroContactID(eventKey!)
									props.handleSettingsUpdate(props.stripeOrgID, {
										'stripeFeesXeroContactXeroID': eventKey!,
										'xeroContact': xeroContacts.find(c => c.contactID === eventKey)
									})
								}}
								disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
							>
								{xeroContacts.map((contact) => (
									<Dropdown.Item key={contact.contactID} eventKey={contact.contactID}>
										{contact.name}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Col>
					</>
				) : null}
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Account
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={'settings-account-select'}
						title={
							props.mappedXeroOrg!.accounts.find(
								(a) => a.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroAccountID
							)?.name || 'Select Xero Account'
						}
						onSelect={(eventKey) =>
							props.handleSettingsUpdate(props.stripeOrgID, { 'stripeFeesXeroAccountID': eventKey! })
						}
						disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
					>
						<Dropdown.Header>Direct Costs</Dropdown.Header>
						{props.mappedXeroOrg!.accounts
							.filter((account) => account.type === 'DIRECTCOSTS')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Expenses</Dropdown.Header>
						{props.mappedXeroOrg!.accounts
							.filter((account) => account.type === 'EXPENSE')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={'settings-taxrate-select'}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) =>
									tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) =>
							props.handleSettingsUpdate(props.stripeOrgID, { 'stripeFeesXeroTaxRateID': eventKey! })
						}
						disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
					>
						{props.mappedXeroOrg!.taxRates
							.filter((tr) => tr.canApplyToExpenses)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
		</>
	)
}

export { OrgMappingHeader, OrgMappingContent }